// ============================================
// Provision Alignment Map
// Cross-document provision pairs and inherited
// provision blocks for the GC Agent.
// ============================================

import type { ProvisionPair, InheritedProvision } from '../../types/docops';

// ── PROVISION ALIGNMENT MAP ──
// 15 pairs that must stay consistent across the document library.
// The GC Agent checks these after every generation round.

export const PROVISION_ALIGNMENT_MAP: ProvisionPair[] = [
  // ── Consent ──
  {
    id: 'pa-consent-tos-privacy',
    documentA: '1.1-terms-of-service',
    sectionA: '§5 Consent Model',
    documentB: '1.2-privacy-policy',
    sectionB: '§Data Collection',
    whatMustAlign: 'Same 7 purposes, same required/optional designation',
    severity: 'critical',
  },
  {
    id: 'pa-consent-tos-chd',
    documentA: '1.1-terms-of-service',
    sectionA: '§5 Consent Model',
    documentB: '1.3-consumer-health-data-policy',
    sectionB: '§Consent',
    whatMustAlign: 'MHMDA consent requirements reflected',
    severity: 'critical',
  },

  // ── Liability ──
  {
    id: 'pa-liability-tos-msa',
    documentA: '1.1-terms-of-service',
    sectionA: '§9 Clinical Liability',
    documentB: '4.1-management-services-agreement',
    sectionB: '§Indemnification',
    whatMustAlign: 'Liability allocation consistent',
    severity: 'critical',
  },
  {
    id: 'pa-liability-tos-md',
    documentA: '1.1-terms-of-service',
    sectionA: '§9 Clinical Liability',
    documentB: '4.3-medical-director-agreement',
    sectionB: '§Scope',
    whatMustAlign: 'MD role matches TOS description',
    severity: 'critical',
  },

  // ── Intellectual Property ──
  {
    id: 'pa-ip-tos-license',
    documentA: '1.1-terms-of-service',
    sectionA: '§7 IP',
    documentB: '3.3-ip-license-agreement',
    sectionB: '§Grant',
    whatMustAlign: 'License scope, exclusivity, restrictions match',
    severity: 'critical',
  },
  {
    id: 'pa-ip-tos-opag',
    documentA: '1.1-terms-of-service',
    sectionA: '§7 IP Owner Beneficiary',
    documentB: '2.1-operating-agreement',
    sectionB: '§IP',
    whatMustAlign: 'Same IP owner rights',
    severity: 'high',
  },

  // ── Telehealth ──
  {
    id: 'pa-telehealth-tos-consent',
    documentA: '1.1-terms-of-service',
    sectionA: '§3.2 Telehealth',
    documentB: '1.7-telehealth-informed-consent',
    sectionB: '§Service',
    whatMustAlign: 'Provider network, fee, disclaimers match',
    severity: 'high',
  },

  // ── Arbitration ──
  {
    id: 'pa-arbitration-tos-privacy',
    documentA: '1.1-terms-of-service',
    sectionA: '§10 Arbitration',
    documentB: '1.2-privacy-policy',
    sectionB: '§Disputes',
    whatMustAlign: 'Same arbitration terms or cross-reference',
    severity: 'high',
  },

  // ── Deletion / Retention ──
  {
    id: 'pa-deletion-tos-privacy',
    documentA: '1.1-terms-of-service',
    sectionA: '§6 Account Deletion',
    documentB: '1.2-privacy-policy',
    sectionB: '§Retention',
    whatMustAlign: 'Deletion timelines consistent',
    severity: 'high',
  },

  // ── Data / BAA ──
  {
    id: 'pa-data-privacy-baa',
    documentA: '1.2-privacy-policy',
    sectionA: '§Sharing',
    documentB: '5.1-baa-llc-pc',
    sectionB: '§Permitted Uses',
    whatMustAlign: 'Covered data types match',
    severity: 'critical',
  },
  {
    id: 'pa-entity-baa-msa',
    documentA: '5.1-baa-llc-pc',
    sectionA: '§Covered Entity',
    documentB: '4.1-management-services-agreement',
    sectionB: '§Parties',
    whatMustAlign: 'Same entity as covered entity',
    severity: 'high',
  },

  // ── Equity ──
  {
    id: 'pa-equity-opag-employment',
    documentA: '2.1-operating-agreement',
    sectionA: '§Equity',
    documentB: '7.1-employment-agreement',
    sectionB: '§Equity',
    whatMustAlign: 'Percentages consistent',
    severity: 'medium',
  },

  // ── Change of Control ──
  {
    id: 'pa-coc-opag-tos',
    documentA: '2.1-operating-agreement',
    sectionA: '§Change of Control',
    documentB: '1.1-terms-of-service',
    sectionB: '§13.4 Assignment',
    whatMustAlign: 'Acquisition mechanics align',
    severity: 'high',
  },

  // ── MD Compensation ──
  {
    id: 'pa-comp-md-opag',
    documentA: '4.3-medical-director-agreement',
    sectionA: '§Compensation',
    documentB: '2.1-operating-agreement',
    sectionB: '§Expenses',
    whatMustAlign: 'MD comp reflected as operating expense',
    severity: 'medium',
  },

  // ── MD Scope ──
  {
    id: 'pa-scope-md-tos',
    documentA: '4.3-medical-director-agreement',
    sectionA: '§Scope',
    documentB: '1.1-terms-of-service',
    sectionB: '§Medical Director Role',
    whatMustAlign: 'Same role description',
    severity: 'high',
  },

  // ── SOC 2 Cross-References ──
  {
    id: 'pa-retention-classification-retention',
    documentA: '13.4-data-classification-policy',
    sectionA: '§Data Retention by Classification',
    documentB: '8.3-data-retention-policy',
    sectionB: '§Retention Schedule',
    whatMustAlign: 'Retention periods per data category must match',
    severity: 'high',
  },
  {
    id: 'pa-access-infosec-access',
    documentA: '13.1-information-security-policy',
    sectionA: '§Platform Security Architecture',
    documentB: '13.2-access-control-policy',
    sectionB: '§Role Definitions',
    whatMustAlign: 'Role model and access controls consistent',
    severity: 'high',
  },
  {
    id: 'pa-vendor-infosec-vendor',
    documentA: '13.1-information-security-policy',
    sectionA: '§Risk Management Overview',
    documentB: '13.6-vendor-management-policy',
    sectionB: '§Vendor Register',
    whatMustAlign: 'Vendor list and risk assessments consistent',
    severity: 'medium',
  },
  {
    id: 'pa-incident-infosec-runbook',
    documentA: '13.1-information-security-policy',
    sectionA: '§Incident Response Overview',
    documentB: '8.7-incident-response-runbook',
    sectionB: '§Incident Type 1',
    whatMustAlign: 'Incident response procedures consistent',
    severity: 'high',
  },
];

// ── INHERITED PROVISIONS ──
// Provision blocks injected into generation prompts for every
// document that lists the provision ID in its inheritedProvisions array.

export const INHERITED_PROVISIONS: InheritedProvision[] = [
  {
    id: 'cpom-firewall',
    name: 'CPOM Firewall',
    description: 'Corporate Practice of Medicine firewall language',
    appliesTo: [
      '1.1-terms-of-service',
      '1.2-privacy-policy',
      '4.1-management-services-agreement',
      '4.3-medical-director-agreement',
      '1.7-telehealth-informed-consent',
      '5.1-baa-llc-pc',
    ],
    keywords: [
      'does not practice medicine',
      'technology platform',
      'does not employ',
      'does not control',
      'clinical decisions',
      'independent clinical judgment',
    ],
    promptBlock: [
      'CPOM FIREWALL LANGUAGE — MUST INCLUDE IN ANY DOCUMENT THAT REFERENCES CLINICAL SERVICES:',
      '- HemoLogix is a technology platform, not a medical practice',
      '- HemoLogix does not practice medicine',
      '- HemoLogix does not employ prescribing physicians or healthcare practitioners',
      '- HemoLogix does not control or direct clinical decisions',
      '- Clinical services provided by independent practitioners in the OpenLoop network',
      '- Medical Director provides protocol oversight and quality assurance (NOT supervision, NOT direction, NOT control)',
    ].join('\n'),
  },
  {
    id: 'ip-licensed-model',
    name: 'IP Licensed Model',
    description: 'Intellectual property ownership and licensing structure',
    appliesTo: [
      '1.1-terms-of-service',
      '2.1-operating-agreement',
      '3.3-ip-license-agreement',
      '7.1-employment-agreement',
    ],
    keywords: [
      'intellectual property owner',
      'licensed to',
      'exclusive license',
      'third-party beneficiary',
      'IP Owner',
    ],
    promptBlock: [
      'IP OWNERSHIP LANGUAGE — MUST INCLUDE IN ANY DOCUMENT THAT REFERENCES PLATFORM IP:',
      '- Platform Technology owned by third-party IP Owner',
      '- Licensed to HemoLogix LLC under exclusive license agreement',
      '- HemoLogix operates pursuant to this license',
      '- IP Owner is third-party beneficiary with enforcement rights',
      '- Any assignment/acquisition requires separate IP negotiation with IP Owner',
    ].join('\n'),
  },
  {
    id: 'clinical-liability-carveout',
    name: 'Clinical Liability Carve-Out',
    description: 'Clinical liability allocation to treating provider',
    appliesTo: [
      '1.1-terms-of-service',
      '4.1-management-services-agreement',
      '4.3-medical-director-agreement',
      '1.7-telehealth-informed-consent',
    ],
    keywords: [
      'not liable for clinical decisions',
      'treating provider',
      'independent clinical judgment',
      'clinical services',
    ],
    promptBlock: [
      'CLINICAL LIABILITY LANGUAGE — MUST INCLUDE IN ANY DOCUMENT THAT ADDRESSES LIABILITY FOR CLINICAL SERVICES:',
      '- HemoLogix not liable for clinical decisions, diagnoses, prescriptions, or medical advice',
      '- Liability rests with treating provider and/or telehealth network (OpenLoop)',
      '- Healthcare practitioners exercise independent clinical judgment',
      '- Claims for clinical services directed to treating provider, not HemoLogix',
    ].join('\n'),
  },
  {
    id: 'consent-model-reference',
    name: 'Consent Model Reference',
    description: '7-purpose consent model alignment with TOS',
    appliesTo: [
      '1.2-privacy-policy',
      '1.3-consumer-health-data-policy',
      '1.4-hipaa-npp',
      '1.7-telehealth-informed-consent',
    ],
    keywords: [
      'core service',
      'health data analysis',
      'marketing communications',
      'withdraw consent',
      'consent purposes',
    ],
    promptBlock: [
      'CONSENT MODEL LANGUAGE — MUST ALIGN WITH TOS §5:',
      '- Reference the 7 consent purposes by number and name',
      '- Maintain required/optional designation exactly as TOS defines',
      '- Do not add new purposes without flagging as "NEW CONSENT PURPOSE"',
      '- Do not omit any purpose the TOS includes',
      '- Withdrawal mechanics must match TOS (optional purposes can be withdrawn; required purposes withdrawal = account deactivation)',
    ].join('\n'),
  },
];
